import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import { useIsMutating } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Layout } from '../../components/layout';
import { Edit, RightSidebar, View } from './components';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import './Draft.styles.css';

import { TypeModal } from './components/type-modal';
import { RegulationsActionsBar } from './components/regulations-actions-bar';
import type { DraftFormValues } from './Draft.types.ts';
import { useService } from './Draft.hooks.ts';

type Link = { title: string; url: string };

export default function DraftRegulation() {
  const [showTypeModal, setShowTypeModal] = useState(true);
  const [regType, setRegType] = useState<number>();
  const navigate = useNavigate();
  const location = useLocation();
  const isView = location.pathname.includes('view');
  const { id } = useParams();
  const { createMutation, updateMutation } = useService({ regulationId: null });
  const isSavingRef = useRef(false);
  const { t } = useTranslation();
  const isSavingDraft = useIsMutating({ mutationKey: ['saveDraftRegulation'] }) > 0;
  const isPublishing = useIsMutating({ mutationKey: ['publishDraftRegulation'] }) > 0;
  const isSaving = isSavingDraft || isPublishing;
  const spinnerTip = isPublishing ? t('common.publishing-draft') : t('common.saving-draft');

  const handleSubmit = (values: DraftFormValues) => {
    if (
      isSavingRef.current ||
      createMutation.isPending ||
      updateMutation.isPending
    ) {
      return;
    }

    isSavingRef.current = true;

    const onSettled = () => {
      isSavingRef.current = false;
    };

    const formData = new FormData();
    formData.append('title', values.title);
    if (values.type) formData.append('type', values.type?.toString());
    formData.append('titleEnglish', values.titleEnglish);
    formData.append('unitInChargeId', String(values.unitInChargeId)); // ensure it's a string
    if (values.author?.[0]) formData.append('author.entraId', values.author[0]?.value ?? '');
    if (values.author?.[0]) formData.append('author.displayName', values.author[0]?.label ?? '');
    if (values.author?.[0]) formData.append('author.email', values.author[0]?.key ?? '');

    if (values.creationDate) {
      formData.append('creationDate', dayjs(values.creationDate).format('YYYY-MM-DD'));
    }
    formData.append('emergedOut', values.emergedOut);
    if (values.targetGroupId) formData.append('targetGroupId', values.targetGroupId);

    values.topics.forEach((topic, index) => {
      formData.append(`topics[${index}]`, topic);
    });

    if (values.involvedUnits) {
      values.involvedUnits.forEach((unit, index) => {
        formData.append(`involvedUnits[${index}]`, unit.toString());
      });
    }
    values.links.forEach((link: Link, index: number) => {
      formData.append(`links[${index}].title`, link.title);
      formData.append(`links[${index}].url`, link.url);
    });

    formData.append('content', values.content.toString());
    formData.append('shortDescription', values.shortDescription.toString());
    formData.append('shortDescriptionEnglish', values.shortDescriptionEnglish.toString());
    if (values?.id) {
      /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
      /* @ts-ignore @ts-expect-error */
      values.attachments.forEach((file: Blob) => {
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        /* @ts-ignore @ts-expect-error */
        if (file.id) {
          /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
          /* @ts-ignore @ts-expect-error */
          formData.append('existingAttachmentIds', file.id);
          return;
        }
        formData.append('newAttachments', file);
      });
      formData.append('id', values.id.toString());
      updateMutation.mutate(formData, { onSettled });
      return;
    }

    /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
    /* @ts-ignore @ts-expect-error */
    values.attachments.forEach((file: Blob) => {
      formData.append('attachments', file);
    });

    createMutation.mutate(formData, { onSettled });
  };

  const handleSubmitTypeModal = ({ type }: { type: number }) => {
    setRegType(type);
    setShowTypeModal(false);
  };
  const handleCloseTypeModal = () => {
    setShowTypeModal(false);
    navigate('/');
  };

  useEffect(() => {
    document.title = `Entwurf`;
  }, []);

  return (
    <>
      {isSaving && <Spin fullscreen size="large" tip={spinnerTip} />}
      <Layout leftSidebar={null} rightSidebar={<RightSidebar />}>
        <div className="draft-regulation-container">
          <RegulationsActionsBar />
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore @ts-expect-error */}
          {!isView ? <Edit onSubmit={handleSubmit} regType={regType} /> : <View />}

          {!isView && !id && showTypeModal && (
            <TypeModal
              open={showTypeModal}
              /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
              /* @ts-ignore @ts-expect-error */
              onSubmit={handleSubmitTypeModal}
              onClose={handleCloseTypeModal}
            />
          )}
        </div>
      </Layout>
    </>
  );
}
